import React, { useMemo, useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import toLower from 'lodash/toLower'

import InputAdornment from '@material-ui/core/InputAdornment'
import SearchIcon from '@material-ui/icons/Search'

import fertilizerSchema from '@galo-monitor/forms/schemas/root/fertilizer.schema'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { FertilizerActions } from '@galo-monitor/stores/fertilizer'
import colors from '@galo-monitor/styles/colors'
import Form from '@galo-monitor/web-components/Form'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'
import RadioGroup from '@galo-monitor/web-components/RadioGroup'

import { Container } from './theme'

const FertilizerForm = forwardRef((props, formRef) => {
  const {
    fertilizer,
    loading,
    setLoading,
    onSuccess,
    onError
  } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const { data: manufacturers, isValidating } = useGaloMonitorApi('/admin/fertilizer-makers')

  const manufacturersOptions = useMemo(
    () => !isEmpty(manufacturers) > 0
      ? map(manufacturers, ({ name }) => ({
        label: name,
        value: name
      }))
      : [],
    [manufacturers]
  )

  const fertilizerTypesOptions = useMemo(
    () => [
      {
        label: t('solid'),
        value: 'solid'
      },
      {
        label: t('liquid'),
        value: 'liquid'
      }
    ],
    [t]
  )

  const originOptions = useMemo(
    () => [
      {
        label: t('mineral'),
        value: 'mineral'
      },
      {
        label: t('organic'),
        value: 'organic'
      }
    ],
    [t]
  )

  const handleSubmit = useCallback(
    (data) => {
      setLoading(true)

      dispatch(FertilizerActions.saveFertilizer(
        {
          ...fertilizer,
          ...data
        },
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: t('fertilizer', { howMany: 1 }),
            action: toLower(t(isEmpty(fertilizer) ? 'created' : 'updated', { gender: 'male', howMany: 1 }))
          }))
          onSuccess()
        },
        onError
      ))
    },
    [fertilizer, dispatch, onError, onSuccess, setLoading, snackbar, t]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ fertilizerSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <InputText
          name="name"
          label={ t('trade name') }
          defaultValue={ fertilizer?.name ?? '' }
          disabled={ loading }
          fullWidth
        />
        <RadioGroup
          name="fertilizer_type"
          variant="row"
          label={ t('fertilizer type') }
          defaultValue={ fertilizer?.fertilizerType ?? 'solid' }
          options={ fertilizerTypesOptions }
          disabled={ loading }
          fullWidth
        />
        <RadioGroup
          name="origin"
          variant="row"
          label={ t('origin') }
          defaultValue={ fertilizer?.origin ?? 'mineral' }
          options={ originOptions }
          disabled={ loading }
          fullWidth
        />
        <InputSelect
          name="fertilizer_maker_name"
          label={ t('manufacturer', { howMany: 1 }) }
          options={ manufacturersOptions }
          defaultValue={ fertilizer?.fertilizerMaker ?? '' }
          disabled={ loading || isValidating }
          InputProps={ {
            endAdornment: (
              <InputAdornment position="end" style={ { marginRight: -25 } }>
                <SearchIcon style={ { color: colors.lightGrey4 } } />
              </InputAdornment>
            )
          } }
          creatable
          disableClearable
          fullWidth
        />
      </Container>
    </Form>
  )
})

FertilizerForm.propTypes = {
  fertilizer: PropTypes.object,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

FertilizerForm.defaultProps = {
  fertilizer: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default FertilizerForm
